import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { useParams } from 'react-router-dom';
import { RendererContext } from '../../contexts/RendererContext';
import { Point, Measure } from '../../potree/utils/Measure';
import { useCustomSelector } from '../../redux/store';
import { useDispatch } from 'react-redux';
import { toggleSelectedAnnotation, setSelectedWallSketchElement, setSelectedAnnotations, mergeSelectedAnnotations, removeSelectedAnnotations } from '../../redux/rendererReducer';
import { FloorHeightInfo } from '../../potree/utils/skeleton/resolveSkeletonHeight';
import { useUserDefaultColor } from '../../modules/Properties/integration/UserSettingsIntegration';
import { ProjectShellFragment, ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables, GroupType, SkeletonWallType } from '../../types/graphqlTypes';
import { PROJECT_ANNOTATION_STATE } from '../../graphql/project';

import { useCreateAnnotation } from './useCreateAnnotation';
import { useDeleteAnnotation } from './useDeleteAnnotation';
import { useGroupOperations } from './useGroupOperations';
import {
  PropertyType,
  getGlobalPropertyService,
  hasGlobalPropertyService,
  getDistancePointsProperty,
  getAreaPointsProperty,
  getPointClusterSegmentsProperty,
  getRoomPolygonProperty,
  getRoomBaseProperty,
  getRoomCeilingProperty,
  getRoomGroupPositionProperty,
  getRoomGroupLinkProperty,
  ROOM_GROUP_LINK_NONE,
  getWallSketchNodesProperty,
  getWallSketchEdgesProperty,
  getWallSketchMetadataProperty,
  getOpeningKindProperty,
  getOpeningWallSketchEdgeIdProperty,
  getOpeningSketchWallEdgeIdProperty,
  getOpeningSketchOffsetProperty,
  getOpeningSketchLengthProperty,
  getOpeningSketchSwingPositiveProperty,
  getOutlineOuterRingProperty,
  getOutlineCavitiesProperty,
  getOutlineFloorHeightProperty,
  getStructurePolygonProperty,
} from '@pointorama/potree-shared';
import { OpeningKind, StructureKind } from 'shared-types';
import { resolveRoomGroupForPoint, resolveRoomGroupForOpeningSpan, resolveRoomGroupForWallEdge, wallObjectCenterPoint } from '../../potree/utils/resolveRoomForPoint';
import type { RoomTagEditRequest } from '../../utils/roomFolders';
import {
  buildCreatePointParams,
  buildCreateBoxParams,
  buildCreateDistanceParams,
  buildCreateAreaParams,
  buildCreateOpeningParams,
  buildCreatePointClusterParams,
  buildCreateRoomParams,
  buildCreateStructureParams,
  buildCreateWallSketchParams,
  buildCreateOpeningSketchParams,
  buildCreateSketchObjectParams,
  buildCreateOutlineParams,
} from './annotationFactories';
import type { OpeningSketchRequest } from '../../potree/utils/skeleton/replicateOpeningSketches';
// Side-effect import: registers the sketch-object type catalog (and its param
// properties) before any panel/registry lookup touches SKETCH_OBJECT data.
import '../../potree/sketchObjects';
import { getSketchObjectType } from '../../potree/sketchObjects';
import { t } from '../../potree/translation/markers';
import type { SketchObjectRequest } from '../../potree/sketchObjects/constraints/types';
import { PointoramaSketchObject } from '../../potree/utils/PointoramaSketchObject';
import { useAnnotationLookup, useAnnotationTree } from './useAnnotationContext';
import { isAnnotationContextGroup } from '../../contexts/AnnotationContext';
import { useSelectAnnotation } from './useSelectAnnotation';
import { useStableEvent } from '../useStableEvent';
import { useFlatAnnotationList } from './useFlatAnnotationList';
import { useSelectCadItem } from './useSelectCadItem';
import { PointCluster } from '../../potree/utils/PointCluster';
import { PointoramaPlane } from '../../potree/utils/PointoramaPlane';
import { PointoramaAnnotation, OpeningAnnotation } from '../../potree/utils/PointoramaAnnotation';
import { PointoramaRoom } from '../../potree/utils/PointoramaRoom';
import { PointoramaOpeningSketch } from '../../potree/utils/PointoramaOpeningSketch';
import { CadObject } from '../../potree/utils/CadObject';
import { BoxVolume, Volume } from '../../potree/utils/Volume';
import { ensureClipMethodPropertyRegistered } from '@pointorama/potree-shared';
import { ensureFilterTogglePropertyRegistered } from '../../modules/Properties/integration/annotations/FilterToggleProperty';
import { useT } from '../../translation/src';
import { useHintBar } from '../useHintBar';
import { useToastNotifications, ToastType } from '../useToastNotifications';
import { v4 as uuid } from 'uuid';
import { useFloorProperties } from '../../modules/Properties/integration/annotations/FloorProperties';
import { useFloorGroupsData } from './useFloorGroupsData';
import { useSurfaceProperties } from '../../modules/Properties/integration/annotations/SurfaceProperties';
import { useSurfaceGroupsData } from './useSurfaceGroupsData';
import { SurfaceKind } from 'shared-types';

/** Subset of DetectedFloor from potree_fork/src/utils/FloorDetection.ts */
interface DetectedFloor {
  height: number;
  center: { x: number; y: number; z: number };
  normal: { x: number; y: number; z: number };
  pointCount: number;
  confidence: number;
}

/** Floor plane data from FloorGroupData */
interface FloorPlaneData {
  height: number;
  center: { x: number; y: number; z: number };
  normal: { x: number; y: number; z: number };
  pointCount: number;
  confidence: number;
  bounds?: {
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
  };
}

const useTranslations = () => {
  const point = useT('point', { swc: true });
  const line = useT('line', { swc: true });
  const polygon = useT('polygon', { swc: true });
  const box = useT('box', { swc: true });
  const pointCluster = useT('point cluster', { swc: true });
  const plane = useT('plane', { swc: true });

  // Hint translations
  const selectPlaneForFloorplan = useT('Choose a plane to use as a base for creating a floorplan');
  const warningNoGroundPlaneSelected = useT('Please select a reference plane first before using the floorplan tool');
  const warningNoGroundPlaneTitle = useT('No Reference Plane Selected');
  const warningDoorWindowOverlapTitle = useT('Overlapping opening');
  const warningDoorWindowOverlapText = useT('Overlapping doors and/or windows are not possible');
  const floorAddedTitle = useT('Floor Added');
  // Templates — `{height}` is substituted at event time.
  const floorAddedText = useT('Floor {height}m has been created');
  const floorName = useT('Floor {height}m');
  const boxRevertedTitle = useT('Box Reverted');
  const boxRevertedText = useT('The box extrusion was reverted because it would break the shape');
  const alignmentRevertedTitle = useT('Alignment Not Applied');
  const alignmentRevertedText = useT('The alignment was reverted because the remaining geometry does not allow the adjustment');

  // Default names for auto-classified structures
  const beam = useT('beam', { swc: true });
  const column = useT('column', { swc: true });
  const structure = useT('structure', { swc: true });

  // Default names for auto-created annotations/groups
  const outerWall = useT('Outer wall');
  const roomAsBuilt = useT('Room as-built');
  const wallSketch = useT('Wall sketch');
  const doorName = useT('Door');
  const windowName = useT('Window');
  const roomName = useT('room', { swc: true });
  const sectionName = useT('Section');
  const surfaceName = useT('Surface');

  return {
    point,
    line,
    polygon,
    box,
    pointCluster,
    plane,
    selectPlaneForFloorplan,
    warningNoGroundPlaneSelected,
    warningNoGroundPlaneTitle,
    warningDoorWindowOverlapTitle,
    warningDoorWindowOverlapText,
    floorAddedTitle,
    floorAddedText,
    floorName,
    boxRevertedTitle,
    boxRevertedText,
    alignmentRevertedTitle,
    alignmentRevertedText,
    beam,
    column,
    structure,
    outerWall,
    roomAsBuilt,
    wallSketch,
    doorName,
    windowName,
    roomName,
    sectionName,
    surfaceName,
  };
};

export const usePotreeEvents = ({ project }: { project?: ProjectShellFragment }) => {
  const translations = useTranslations();
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;
  const apolloClient = useApolloClient();
  const { projectId: routeProjectId = '' } = useParams();
  // Read raw annotation state from cache on demand (no subscription — context handles reactivity)
  const readAnnotationState = useCallback(() => {
    try {
      return apolloClient.readQuery<ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables>({
        query: PROJECT_ANNOTATION_STATE,
        variables: { projectId: routeProjectId },
      })?.projectById;
    } catch {
      return undefined;
    }
  }, [apolloClient, routeProjectId]);
  const annotationStateRef = useRef(readAnnotationState());
  useEffect(() => {
    annotationStateRef.current = readAnnotationState();
  }, [readAnnotationState]);
  const createAnnotation = useCreateAnnotation();
  const { deleteAnnotation } = useDeleteAnnotation();
  const dispatch = useDispatch();
  const { colorHex: defaultColorHex } = useUserDefaultColor();
  const { createGroup, moveAnnotationsToGroup } = useGroupOperations();
  const annotationsByIdentifier = useAnnotationLookup();
  const annotationsByIdentifierRef = useRef(annotationsByIdentifier);
  useEffect(() => {
    annotationsByIdentifierRef.current = annotationsByIdentifier;
  }, [annotationsByIdentifier]);
  const { selectedAnnotations } = useCustomSelector((state) => state.rendererProvider, ['selectedAnnotations']);
  const selectedAnnotationsRef = useRef(selectedAnnotations);
  useEffect(() => {
    selectedAnnotationsRef.current = selectedAnnotations;
  }, [selectedAnnotations]);
  const selectedAnnotationIdentifier = selectedAnnotations.length === 1 && selectedAnnotations[0].type === 'annotation' ? selectedAnnotations[0].identifier : undefined;
  const selectedAnnotationIdentifierRef = useRef(selectedAnnotationIdentifier);
  useEffect(() => {
    selectedAnnotationIdentifierRef.current = selectedAnnotationIdentifier;
  }, [selectedAnnotationIdentifier]);
  const selectedGroupIdentifier = selectedAnnotations.length === 1 && selectedAnnotations[0].type === 'group' ? selectedAnnotations[0].identifier : undefined;
  const selectedGroupIdentifierRef = useRef(selectedGroupIdentifier);
  useEffect(() => {
    selectedGroupIdentifierRef.current = selectedGroupIdentifier;
  }, [selectedGroupIdentifier]);
  const { onClickAnnotation } = useSelectAnnotation();
  const { onClickCadItem } = useSelectCadItem();
  const { allSelectedAnnotations } = useFlatAnnotationList();
  const { selectedCadItems } = useCustomSelector((state) => state.rendererProvider, ['selectedCadItems']);
  const { setFloorPropertiesBatch } = useFloorProperties(project?.id || '');
  const { loadFloorGroupsData } = useFloorGroupsData(project);
  const { setSurfacePropertiesBatch } = useSurfaceProperties(project?.id || '');
  const { loadSurfacesFromGroups } = useSurfaceGroupsData(project);

  // Feed every scene skeleton the project's floor heights so per-element height
  // constraints can resolve `default` (next floor / ceiling) and explicit
  // target-floor references during 3D extrusion. Re-runs whenever the floor
  // groups change (loadFloorGroupsData's identity tracks the annotation tree).
  useEffect(() => {
    if (!viewer?.scene?.wallSketches) return;
    const floors: FloorHeightInfo[] = loadFloorGroupsData().map((g) => ({
      groupIdentifier: g.groupIdentifier,
      floorHeight: g.floor.height,
      ceilingHeight: g.ceiling?.height,
    }));
    for (const skeleton of viewer.scene.wallSketches) {
      skeleton.setFloorGroupsData(floors);
    }
  }, [loadFloorGroupsData, viewer]);

  useEffect(() => {
    if (!viewer?.scene?.measurements) return;
    viewer.scene.measurements.forEach((measurement) => {
      if (measurement instanceof PointoramaAnnotation) {
        if (allSelectedAnnotations.some((annotation) => annotation.identifier === measurement.identifier)) {
          measurement.select();
        } else {
          measurement.deselect();
        }
      } else if (measurement instanceof CadObject) {
        if (selectedCadItems?.some((cadItem) => cadItem.identifier === measurement.identifier)) {
          measurement.select();
        } else {
          measurement.deselect();
        }
      }
    });
    viewer.scene.volumes.forEach((volume) => {
      const selectedPointorama = allSelectedAnnotations.some((annotation) => volume.identifier === annotation.identifier);
      const selectedPotree = volume.selected;
      if (selectedPointorama && !selectedPotree) {
        volume.select();
      } else if (!selectedPointorama && selectedPotree) {
        volume.unselect();
      }
    });
    viewer.scene.pointClusters.forEach((pointCluster) => {
      const selectedPointCluster = allSelectedAnnotations.some((annotation) => pointCluster.identifier === annotation.identifier);

      if (selectedPointCluster) {
        pointCluster.select();
      } else {
        pointCluster.unselect();
      }
    });
    viewer.scene.planes.forEach((plane) => {
      const selectedPlane = allSelectedAnnotations.some((annotation) => plane.identifier === annotation.identifier);

      if (selectedPlane) {
        plane.select();
      } else {
        plane.deselect();
      }
    });
    viewer.scene.rooms.forEach((room) => {
      const selectedRoom = allSelectedAnnotations.some((annotation) => room.identifier === annotation.identifier);

      if (selectedRoom) {
        room.select();
      } else {
        room.deselect();
      }
    });
    viewer.scene.structures.forEach((structure) => {
      const selectedStructure = allSelectedAnnotations.some((annotation) => structure.identifier === annotation.identifier);

      if (selectedStructure) {
        structure.select();
      } else {
        structure.deselect();
      }
    });
    viewer.scene.surfaces.forEach((surface) => {
      // Surfaces are groups, so check against selectedGroupIdentifier
      const isSelected = surface.identifier === selectedGroupIdentifier;

      if (isSelected) {
        surface.select();
      } else {
        surface.deselect();
      }
    });
    viewer.scene.openingSketches.forEach((openingSketch) => {
      const isSelected = allSelectedAnnotations.some((annotation) => openingSketch.identifier === annotation.identifier);

      if (isSelected) {
        openingSketch.select();
      } else {
        openingSketch.deselect();
      }
    });
    viewer.scene.sketchObjects.forEach((sketchObject) => {
      const isSelected = allSelectedAnnotations.some((annotation) => sketchObject.identifier === annotation.identifier);

      if (isSelected) {
        sketchObject.select();
      } else {
        sketchObject.deselect();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSelectedAnnotations, selectedCadItems, selectedGroupIdentifier]);

  // Update in-progress drawing previews when the user's default color changes
  useEffect(() => {
    if (!viewer) return;
    for (const scene of [viewer.measurementScene, viewer.planeScene, viewer.volumeScene]) {
      if (!scene) continue;
      for (const child of scene.children) {
        if ('finished' in child && !child.finished && 'annotationColor' in child) {
          (child as { annotationColor: string }).annotationColor = defaultColorHex;
        }
      }
    }
  }, [viewer, defaultColorHex]);

  const onVolumePlace = useCallback(
    ({ value }: { value?: { volume: Volume } }) => {
      const volume = value?.volume;
      if (!volume) return;
      const currentAnnotationsByIdentifier = annotationsByIdentifierRef.current;
      const currentSelectedAnnotationIdentifier = selectedAnnotationIdentifierRef.current;
      const currentSelectedGroupIdentifier = selectedGroupIdentifierRef.current;
      const selectedAnnotation = currentAnnotationsByIdentifier[currentSelectedAnnotationIdentifier || ''];
      const groupIdentifier = currentSelectedGroupIdentifier || selectedAnnotation?.groupIdentifier;


      createAnnotation(
        buildCreateBoxParams({
          identifier: volume.identifier,
          position: { x: volume.position.x, y: volume.position.y, z: volume.position.z },
          scale: { x: volume.scale.x, y: volume.scale.y, z: volume.scale.z },
          rotation: { x: volume.rotation.x, y: volume.rotation.y, z: volume.rotation.z },
          name: translations.box,
          color: defaultColorHex,
          groupIdentifier,
        }),
      );

      // Register clip properties so the box can respond to filter toggle / clip method changes.
      // syncBoxes.afterCreate does this for boxes loaded from DB, but newly placed boxes
      // already exist in the scene so the sync skips them.
      if (volume instanceof BoxVolume) {
        ensureClipMethodPropertyRegistered(volume.identifier);
        ensureFilterTogglePropertyRegistered(volume.identifier);
        volume.loadReactiveProperties();
      }

      dispatch(
        toggleSelectedAnnotation({
          identifier: volume.identifier,
          clearOtherSelections: true,
          type: 'annotation',
        }),
      );

      return null;
    },
    [createAnnotation, defaultColorHex, dispatch, translations.box],
  );

  const onVolumeMove = useCallback(({ value }: { value?: { volume: Volume } }) => {
    const volume = value?.volume;
    if (!volume) return;

    // Write geometry directly to reactive properties instead of dispatching EDIT_BOX action
    try {
      const service = getGlobalPropertyService();
      if (service) {
        const pos = service.getProperty('box_position', volume.identifier, PropertyType.PROJECT_GLOBAL);
        pos.value = { x: volume.position.x, y: volume.position.y, z: volume.position.z };
        const scale = service.getProperty('box_scale', volume.identifier, PropertyType.PROJECT_GLOBAL);
        scale.value = { x: volume.scale.x, y: volume.scale.y, z: volume.scale.z };
        const rot = service.getProperty('box_rotation', volume.identifier, PropertyType.PROJECT_GLOBAL);
        rot.value = { x: volume.rotation.x, y: volume.rotation.y, z: volume.rotation.z };
      }
    } catch {
      // Property service not ready yet
    }

    return null;
  }, []);

  const onMeasurementFinish = useCallback(
    ({ value }: { value?: { measure: Measure; skipSelection?: boolean; groupIdentifier?: string | null } }) => {
      if (!value) return;
      const { measure, skipSelection } = value;
      const currentAnnotationsByIdentifier = annotationsByIdentifierRef.current;
      // Idempotency guard: `measurement_finish` creates an annotation, so a
      // second dispatch for an id that already exists would add a duplicate
      // (and a duplicate React key). Geometry updates to an existing annotation
      // go through their own per-kind paths (room_updated, getAreaPointsProperty,
      // door_window_updated), never here — so a finish for a known id is always
      // a stray re-dispatch and is safely ignored.
      if (currentAnnotationsByIdentifier[measure.identifier]) return;
      const currentSelectedAnnotationIdentifier = selectedAnnotationIdentifierRef.current;
      const currentSelectedGroupIdentifier = selectedGroupIdentifierRef.current;
      const selectedAnnotation = currentAnnotationsByIdentifier[currentSelectedAnnotationIdentifier || ''];
      // Surfaces are groups, so anything drawn while inside a surface's 2D view
      // should land inside that surface group. (Planes couldn't have children, so
      // this is new.) The surface 2D view builds a virtual clip plane that carries
      // the surface's identifier — so the gate is "associatedPlane.identifier matches
      // a real scene surface", which naturally excludes the map/side (random uuid)
      // and line (DistanceAnnotation id) virtual 2D planes.
      let surface2DGroupIdentifier: string | null = null;
      if (viewer?.viewerMode.in2DMode) {
        const planeId = viewer.viewerMode.associatedPlane?.identifier;
        if (planeId && viewer.scene.surfaces?.some((s) => s.identifier === planeId)) {
          surface2DGroupIdentifier = planeId;
        }
      }
      // An explicit `value.groupIdentifier` (set by tools like the door/window
      // detector that know exactly which floor group the annotation belongs
      // to) wins. Then the active surface 2D view, then the user's sidebar
      // selection, then the currently-selected annotation's group, so
      // user-driven measurement-tool flows behave as before.
      const groupIdentifier = value.groupIdentifier ?? surface2DGroupIdentifier ?? currentSelectedGroupIdentifier ?? selectedAnnotation?.groupIdentifier;
      if (measure.type === 'POINT') {
        const point = measure.points[0];
        if (!point) return;
        const pickColor = point.color ? `#${((1 << 24) | (point.color[0] << 16) | (point.color[1] << 8) | point.color[2]).toString(16).slice(1)}` : '#ffffff';

        createAnnotation(
          buildCreatePointParams({
            identifier: measure.identifier,
            position: { x: point.position.x, y: point.position.y, z: point.position.z },
            pickColor,
            classification: point.classification?.[0] ?? 0,
            intensity: point.intensity?.[0] ?? 0,
            pointCloudId: point.pointCloudId || '',
            name: measure.name || translations.point,
            color: defaultColorHex,
            groupIdentifier,
          }),
        );

        if (!skipSelection) {
          dispatch(
            toggleSelectedAnnotation({
              identifier: measure.identifier,
              clearOtherSelections: true,
              type: 'annotation',
            }),
          );
        }
      } else if (measure.type === 'DISTANCE') {
        createAnnotation(
          buildCreateDistanceParams({
            identifier: measure.identifier,
            points: measure.points.map((point) => ({
              position: { x: point.position.x, y: point.position.y, z: point.position.z },
              pointCloudId: point.pointCloudId || null,
            })),
            name: measure.name || translations.line,
            color: defaultColorHex,
            groupIdentifier,
          }),
        );
        if (!skipSelection) {
          dispatch(
            toggleSelectedAnnotation({
              identifier: measure.identifier,
              clearOtherSelections: true,
              type: 'annotation',
            }),
          );
        }
      } else if (measure instanceof OpeningAnnotation) {
        // Read the kind / skeleton link the creator (DoorWindowDetectionService,
        // surface tool) wrote to the reactive properties before finishMeasurement.
        // Routing them through `initialProperties` keeps creation atomic and
        // avoids a write race against the create mutation.
        const kindValue = (() => {
          try {
            return getOpeningKindProperty(measure.identifier).value as OpeningKind;
          } catch {
            return OpeningKind.DOOR;
          }
        })();
        const wallSketchEdgeIdValue = (() => {
          try {
            return getOpeningWallSketchEdgeIdProperty(measure.identifier).value || null;
          } catch {
            return null;
          }
        })();
        createAnnotation(
          buildCreateOpeningParams({
            identifier: measure.identifier,
            points: measure.points.map((point) => ({
              position: { x: point.position.x, y: point.position.y, z: point.position.z },
              pointCloudId: point.pointCloudId || null,
            })),
            name: measure.name || (kindValue === OpeningKind.WINDOW ? translations.windowName : translations.doorName),
            color: defaultColorHex,
            kind: kindValue,
            wallSketchEdgeId: wallSketchEdgeIdValue,
            groupIdentifier,
          }),
        );
        if (!skipSelection) {
          dispatch(
            toggleSelectedAnnotation({
              identifier: measure.identifier,
              clearOtherSelections: true,
              type: 'annotation',
            }),
          );
        }
      } else if (measure.type === 'AREA') {
        createAnnotation(
          buildCreateAreaParams({
            identifier: measure.identifier,
            points: measure.points.map((point) => ({
              position: { x: point.position.x, y: point.position.y, z: point.position.z },
              pointCloudId: point.pointCloudId || null,
            })),
            name: measure.name || translations.polygon,
            color: defaultColorHex,
            groupIdentifier,
          }),
        );
        if (!skipSelection) {
          dispatch(
            toggleSelectedAnnotation({
              identifier: measure.identifier,
              clearOtherSelections: true,
              type: 'annotation',
            }),
          );
        }
      }

      return null;
    },
    [createAnnotation, defaultColorHex, dispatch, translations.line, translations.point, translations.polygon, translations.doorName, translations.windowName, viewer],
  );

  /** Write the current geometry of a measure to the correct reactive property */
  const writeMeasurePoints = useCallback((measure: Measure) => {
    try {
      const service = getGlobalPropertyService();
      if (!service) return false;
      if (measure.type === 'POINT') {
        const p = measure.points[0];
        if (p) {
          service.getProperty('point_position', measure.identifier, PropertyType.PROJECT_GLOBAL).value = {
            x: p.position.x,
            y: p.position.y,
            z: p.position.z,
          };
        }
      } else if (measure.type === 'DISTANCE') {
        const points = measure.points.map((p: Point) => ({
          position: { x: p.position.x, y: p.position.y, z: p.position.z },
          pointCloudId: p.pointCloudId || null,
        }));
        getDistancePointsProperty(measure.identifier).value = points;
      } else if (measure.type === 'AREA') {
        const points = measure.points.map((p: Point) => ({
          position: { x: p.position.x, y: p.position.y, z: p.position.z },
          pointCloudId: p.pointCloudId || null,
        }));
        getAreaPointsProperty(measure.identifier).value = points;
      } else {
        return false;
      }
    } catch {
      return false;
    }
    return true;
  }, []);

  const onMeasurementMovePoint = useCallback(
    ({ value }: { value?: { pointIndex: number; point: Point; measure: Measure } }) => {
      if (!value || !value.point) return;
      writeMeasurePoints(value.measure);
    },
    [writeMeasurePoints],
  );

  const onMeasurementAddPoint = useCallback(
    ({ value }: { value?: { pointIndex: number; point: Point; measure: Measure } }) => {
      if (!value) return;
      writeMeasurePoints(value.measure);
    },
    [writeMeasurePoints],
  );

  const onMeasurementDeletePoint = useCallback(
    ({ value }: { value?: { pointIndex: number; measure: Measure } }) => {
      if (!value) return;
      writeMeasurePoints(value.measure);
    },
    [writeMeasurePoints],
  );

  const onMeasurementSetPoints = useCallback(
    ({ value }: { value?: { measure: Measure } }) => {
      if (!value) return;
      writeMeasurePoints(value.measure);
    },
    [writeMeasurePoints],
  );

  type RoomLike = {
    getPolygon: () => { x: number; y: number; z: number }[];
    getBase: () => { position: { x: number; y: number; z: number }; normal: { x: number; y: number; z: number } };
    getCeiling: () => { position: { x: number; y: number; z: number }; normal: { x: number; y: number; z: number } };
    identifier: string;
  };

  /** Write room geometry (polygon, base, ceiling) to reactive properties */
  const writeRoomGeometry = useCallback((room: RoomLike) => {
    try {
      const service = getGlobalPropertyService();
      if (!service) return;
      const polygon = room.getPolygon();
      const base = room.getBase();
      const ceiling = room.getCeiling();
      getRoomPolygonProperty(room.identifier).value = polygon.map((p) => ({ x: p.x, y: p.y, z: p.z }));
      getRoomBaseProperty(room.identifier).value = {
        position: { x: base.position.x, y: base.position.y, z: base.position.z },
        normal: { x: base.normal.x, y: base.normal.y, z: base.normal.z },
      };
      getRoomCeilingProperty(room.identifier).value = {
        position: { x: ceiling.position.x, y: ceiling.position.y, z: ceiling.position.z },
        normal: { x: ceiling.normal.x, y: ceiling.normal.y, z: ceiling.normal.z },
      };
      // Room-folder anchors deliberately do NOT follow polygon edits — the
      // anchor is user-placed (gizmo) and the tag's room association is
      // positional (whichever polygon contains the anchor).
    } catch {
      // Property service not ready yet
    }
  }, []);

  const onRoomMovePoint = useCallback(
    ({ value }: { value?: { pointIndex: number; point: Point; room: RoomLike } }) => {
      if (!value || !value.point) return;
      writeRoomGeometry(value.room);
    },
    [writeRoomGeometry],
  );

  const onRoomAddPoint = useCallback(
    ({ value }: { value?: { pointIndex: number; point: Point; room: RoomLike } }) => {
      if (!value) return;
      writeRoomGeometry(value.room);
    },
    [writeRoomGeometry],
  );

  const onRoomDeletePoint = useCallback(
    ({ value }: { value?: { pointIndex: number; room: RoomLike } }) => {
      if (!value) return;
      writeRoomGeometry(value.room);
    },
    [writeRoomGeometry],
  );

  const onRoomUpdated = useCallback(
    (event: {
      type: string;
      room?: {
        getPolygon: () => { x: number; y: number; z: number }[];
        getBase: () => { position: { x: number; y: number; z: number }; normal: { x: number; y: number; z: number } };
        getCeiling: () => {
          position: { x: number; y: number; z: number };
          normal: { x: number; y: number; z: number };
        };
        identifier: string;
      };
    }) => {
      if (!event.room) return;
      writeRoomGeometry(event.room);
    },
    [writeRoomGeometry],
  );

  /** Write outline geometry (outer ring, cavities, floor height) to reactive properties. */
  const writeOutlineGeometry = useCallback((outline: { identifier: string; getOuterRing: () => { x: number; y: number; z: number }[]; getHoles: () => { x: number; y: number; z: number }[][]; getFloorHeight: () => number }) => {
    try {
      const service = getGlobalPropertyService();
      if (!service) return;
      getOutlineOuterRingProperty(outline.identifier).value = outline.getOuterRing().map((p) => ({ x: p.x, y: p.y, z: p.z }));
      getOutlineCavitiesProperty(outline.identifier).value = outline.getHoles().map((ring) => ring.map((p) => ({ x: p.x, y: p.y, z: p.z })));
      getOutlineFloorHeightProperty(outline.identifier).value = outline.getFloorHeight();
    } catch {
      // Property service not ready
    }
  }, []);

  /**
   * Handle `create_outline` dispatched by OutlineTool: persist one OUTLINE
   * annotation per event. The tool commits one event per connected component,
   * so disconnected wings end up as separate outlines on the same floor.
   *
   * `identifier` may be pre-minted by the dispatching tool (the WallCreateTool
   * needs the id synchronously so it can link the generated wall sketch to its
   * outline without waiting for `outline_added`).
   */
  const onCreateOutline = useCallback(
    ({ value }: { value?: { identifier?: string; outerRing: { x: number; y: number; z: number }[]; cavities: { x: number; y: number; z: number }[][]; floorHeight: number; floorGroupIdentifier: string | null } }) => {
      if (!value || !viewer) return;

      const outlineIdentifier = value.identifier ?? `outline_${value.floorGroupIdentifier ?? Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      createAnnotation(
        buildCreateOutlineParams({
          identifier: outlineIdentifier,
          outerRing: value.outerRing.map((p) => ({ x: p.x, y: p.y, z: p.z })),
          cavities: value.cavities.map((ring) => ring.map((p) => ({ x: p.x, y: p.y, z: p.z }))),
          floorHeight: value.floorHeight,
          name: translations.outerWall,
          color: defaultColorHex,
          groupIdentifier: value.floorGroupIdentifier ?? undefined,
        }),
      );
    },
    [viewer, createAnnotation, defaultColorHex, translations.outerWall],
  );

  const onOutlineUpdated = useCallback(
    (event: { type: string; outline?: { identifier: string; getOuterRing: () => { x: number; y: number; z: number }[]; getHoles: () => { x: number; y: number; z: number }[][]; getFloorHeight: () => number } }) => {
      if (!event.outline) return;
      writeOutlineGeometry(event.outline);
    },
    [writeOutlineGeometry],
  );

  /**
   * Persist a door/window AreaAnnotation's edited polygon. The
   * adapter has already called `AreaAnnotation.exitEditMode(true, polygon)`
   * so `measure.points` reflects the new corners — we just need to flush
   * those points to the reactive property store via writeMeasurePoints.
   */
  const onDoorWindowUpdated = useCallback(
    ({ annotationId }: { annotationId?: string }) => {
      if (!annotationId || !viewer) return;
      const measure = viewer.scene.measurements.find((m: Measure) => m.identifier === annotationId);
      if (!measure) return;
      writeMeasurePoints(measure);
    },
    [viewer, writeMeasurePoints],
  );

  const onMeasurementDeleteAnnotation = useCallback(
    ({ value }: { value?: { measure: Measure } }) => {
      if (!value) return;
      deleteAnnotation(value.measure.identifier);
    },
    [deleteAnnotation],
  );

  const onMeasurementSelectAnnotation = useCallback(
    ({ value }: { value?: { measure: Measure | PointoramaOpeningSketch | PointoramaSketchObject; isCtrl: boolean } }) => {
      if (!value) return;
      if (!value.measure) {
        // Modifier-click (Ctrl/Cmd/Shift) on empty space is a no-op: while
        // accumulating a multi-selection, a missed modifier-click must not
        // wipe it (matches the wall editor's modifier+empty semantics).
        if (value.isCtrl) return;
        // Only deselect if something meaningful is currently selected — avoids
        // creating a new Redux state (and full re-render) on every canvas click.
        // After deselecting, Redux stores [{ identifier: '' }], so check for
        // actual identifiers, not just array length.
        const current = selectedAnnotationsRef.current;
        if (current && current.some((a) => a.identifier !== '')) {
          onClickAnnotation({ identifier: '', type: 'annotation' }, { isCtrl: false, isShift: false });
        }
        return;
      }
      if (
        value.measure instanceof PointoramaAnnotation ||
        value.measure instanceof PointoramaRoom ||
        value.measure instanceof BoxVolume ||
        value.measure instanceof PointCluster ||
        value.measure instanceof PointoramaPlane ||
        value.measure instanceof PointoramaOpeningSketch ||
        value.measure instanceof PointoramaSketchObject
      ) {
        onClickAnnotation({ identifier: value.measure.identifier, type: 'annotation' }, { isCtrl: value.isCtrl, isShift: false });
      } else if (value.measure instanceof CadObject) {
        onClickCadItem({ identifier: value.measure.identifier, type: 'cadObject' }, { isCtrl: value.isCtrl, isShift: false });
      }
    },
    [onClickAnnotation, onClickCadItem],
  );

  /** Box-select commit from a canvas tool: apply a whole set of annotations to
   *  the Redux selection at once. `replace` swaps the selection (an empty box
   *  clears it), `add`/`remove` grow/shrink it (Shift / Ctrl drag). */
  const onAnnotationsBoxSelect = useCallback(
    ({ value }: { value?: { identifiers: string[]; mode: 'replace' | 'add' | 'remove' } }) => {
      if (!value) return;
      const annotations = value.identifiers.map((identifier) => ({ identifier, type: 'annotation' as const }));
      if (value.mode === 'replace') {
        dispatch(setSelectedAnnotations({ annotations }));
      } else if (value.mode === 'add') {
        dispatch(mergeSelectedAnnotations({ annotations }));
      } else {
        dispatch(removeSelectedAnnotations({ identifiers: value.identifiers }));
      }
    },
    [dispatch],
  );

  const onWallSketchElementSelected = useCallback(
    ({ value }: { value?: { annotationIdentifier: string | null; kind: 'node' | 'edge' | null; ids: string[] } }) => {
      if (!value || !value.annotationIdentifier || !value.kind || value.ids.length === 0) {
        dispatch(setSelectedWallSketchElement(undefined));
        return;
      }
      dispatch(setSelectedWallSketchElement({ annotationId: value.annotationIdentifier, kind: value.kind, ids: value.ids }));
    },
    [dispatch],
  );

  const onPointClusterAdded = useCallback(
    ({ value }: { value?: { pointCluster: PointCluster } }) => {
      if (!value) return;
      createAnnotation(
        buildCreatePointClusterParams({
          identifier: value.pointCluster.identifier,
          segments: value.pointCluster.segments,
          name: translations.pointCluster,
          color: defaultColorHex,
        }),
      );
      dispatch(
        toggleSelectedAnnotation({
          identifier: value.pointCluster.identifier,
          clearOtherSelections: true,
          type: 'annotation',
        }),
      );
    },
    [createAnnotation, defaultColorHex, dispatch, translations.pointCluster],
  );

  const onPointClusterEdit = useCallback(({ value }: { value?: { pointCluster: PointCluster } }) => {
    if (!value) return;
    try {
      const service = getGlobalPropertyService();
      if (service) {
        getPointClusterSegmentsProperty(value.pointCluster.identifier).value = value.pointCluster.segments;
      }
    } catch {
      // Property service not ready yet
    }
  }, []);

  const onCreateSurface = useCallback(
    ({
      value,
    }: {
      value?: {
        plane: { position: { x: number; y: number; z: number }; normal: { x: number; y: number; z: number } };
        sourcePlaneIdentifier?: string;
        surfaceKind?: SurfaceKind;
      };
    }) => {
      if (!value) return;

      // Generate a unique identifier for the surface group
      const groupIdentifier = uuid();
      // Use different name based on surface kind
      const surfaceName = value.surfaceKind === SurfaceKind.SECTION ? translations.sectionName : translations.surfaceName;

      // Prepare surface properties
      const surfaceProperties = {
        surfacePlanePosition: value.plane.position,
        surfacePlaneNormal: value.plane.normal,
        surfaceKind: value.surfaceKind,
      };

      // Save properties first, then create the group
      // This ensures properties are available when the surface is created
      setSurfacePropertiesBatch(groupIdentifier, surfaceProperties)
        .then(() => {
          return createGroup({
            identifier: groupIdentifier,
            name: surfaceName,
            type: GroupType.Surface,
            annotationIdentifiers: [],
          });
        })
        .then(() => {
          onClickAnnotation({ identifier: groupIdentifier, type: 'group' }, { isCtrl: false, isShift: false });
        })
        .catch((error) => {
          console.error('[SurfaceCreation] Failed to save surface properties or create group:', error);
        });
    },
    [createGroup, setSurfacePropertiesBatch, onClickAnnotation, translations.sectionName, translations.surfaceName],
  );

  const { showHint } = useHintBar();
  const { showToast } = useToastNotifications();

  const onSetSelectPlaneHint = useCallback(() => {
    showHint(translations.selectPlaneForFloorplan);
  }, [showHint, translations.selectPlaneForFloorplan]);

  const onClearHint = useCallback(() => {
    showHint('');
  }, [showHint]);

  const onFloorplanNoGroundPlaneWarning = useCallback(() => {
    showToast({
      title: translations.warningNoGroundPlaneTitle,
      text: translations.warningNoGroundPlaneSelected,
      type: ToastType.WARNING,
    });
  }, [showToast, translations.warningNoGroundPlaneSelected, translations.warningNoGroundPlaneTitle]);

  const onDoorWindowOverlapRejected = useCallback(() => {
    showToast({
      title: translations.warningDoorWindowOverlapTitle,
      text: translations.warningDoorWindowOverlapText,
      type: ToastType.WARNING,
    });
  }, [showToast, translations.warningDoorWindowOverlapTitle, translations.warningDoorWindowOverlapText]);

  const onCreateFloor = useCallback(
    ({ value }: { value?: { floor: DetectedFloor; ceiling?: DetectedFloor; footprint?: Array<{ x: number; y: number; z: number }> } }) => {
      if (!value) return;
      const { floor, ceiling, footprint } = value;

      // Generate a unique identifier for the floor group
      const groupIdentifier = uuid();
      const floorName = translations.floorName.replace('{height}', floor.height.toFixed(2));

      // Prepare floor properties - force normals to be straight up/down
      const floorProperties: {
        floorPlanePosition?: { x: number; y: number; z: number };
        floorPlaneNormal?: { x: number; y: number; z: number };
        ceilingPlanePosition?: { x: number; y: number; z: number };
        ceilingPlaneNormal?: { x: number; y: number; z: number };
        footprintCorners?: Array<{ x: number; y: number; z: number }>;
      } = {
        floorPlanePosition: {
          x: floor.center.x,
          y: floor.center.y,
          z: floor.height,
        },
        // Force floor normal to point straight up
        floorPlaneNormal: { x: 0, y: 0, z: 1 },
      };

      // Add ceiling properties if available (won't be present for top floor)
      if (ceiling) {
        floorProperties.ceilingPlanePosition = {
          x: ceiling.center.x,
          y: ceiling.center.y,
          z: ceiling.height,
        };
        // Force ceiling normal to point straight down
        floorProperties.ceilingPlaneNormal = { x: 0, y: 0, z: -1 };
      }

      // The floor's own footprint (per-floor bounding area for visualization
      // and clipping). ManualFloorTool doesn't provide one — property stays
      // unset and consumers fall back to the global detection area.
      if (footprint && footprint.length >= 4) {
        floorProperties.footprintCorners = footprint.slice(0, 4);
      }

      // Create the group first, then save properties after it completes
      // Note: create_floor event is only dispatched by AutoFloorTool and ManualFloorTool
      createGroup({
        identifier: groupIdentifier,
        name: floorName,
        type: GroupType.Floor,
        annotationIdentifiers: [],
      })
        ?.then(() => {
          return setFloorPropertiesBatch(groupIdentifier, floorProperties);
        })
        .then(() => {
          // Re-dispatch objects_changed so FloorplanPersona picks up the new floor's
          // plane data. We build the floor data directly from the creation params because
          // the property manager index hasn't re-rendered yet at this point.
          if (!viewer) return;
          const as = readAnnotationState();
          if (!as) return;

          const boundsSize = 10;
          const newFloorData = {
            groupIdentifier,
            name: floorName,
            floor: {
              height: floor.height,
              center: { x: floor.center.x, y: floor.center.y, z: floor.height },
              normal: { x: 0, y: 0, z: 1 },
              bounds: {
                min: { x: floor.center.x - boundsSize / 2, y: floor.center.y - boundsSize / 2, z: floor.height - 0.1 },
                max: { x: floor.center.x + boundsSize / 2, y: floor.center.y + boundsSize / 2, z: floor.height + 0.1 },
              },
              pointCount: 0,
              confidence: 1.0,
            },
            ...(ceiling
              ? {
                  ceiling: {
                    height: ceiling.height,
                    center: { x: ceiling.center.x, y: ceiling.center.y, z: ceiling.height },
                    normal: { x: 0, y: 0, z: -1 },
                    pointCount: 0,
                    confidence: 1.0,
                  },
                }
              : {}),
            ...(floorProperties.footprintCorners ? { footprintCorners: floorProperties.footprintCorners } : {}),
          };

          // Merge the new floor data with existing floors from the property manager
          const existingFloorData = loadFloorGroupsData().filter((f) => f.groupIdentifier !== groupIdentifier);
          const allFloorData = [...existingFloorData, newFloorData].sort((a, b) => a.floor.height - b.floor.height);

          viewer.scene.dispatchEvent({
            type: 'objects_changed',
            groups: as.state.groups || [],
            floorGroupsData: allFloorData,
            surfacesData: loadSurfacesFromGroups(),
          });
        })
        .catch((error) => {
          console.error('[FloorCreation] Failed to create floor group or save properties:', error);
        });
    },
    [createGroup, setFloorPropertiesBatch, viewer, readAnnotationState, loadFloorGroupsData, loadSurfacesFromGroups, translations.floorName],
  );

  // Handle create_room event from AutoRoomDetectTool
  interface Point2D {
    x: number;
    y: number;
  }

  interface Polygon {
    points: Point2D[];
    children?: Polygon[];
  }

  interface Vector3Like {
    x: number;
    y: number;
    z: number;
    clone(): Vector3Like;
    lerp(other: Vector3Like, t: number): Vector3Like;
    sub(other: Vector3Like): Vector3Like;
    dot(other: Vector3Like): number;
    multiplyScalar(scalar: number): Vector3Like;
  }

  interface PlaneDetectionResult {
    distance: number;
    plane: {
      normal: Vector3Like;
      constant: number;
    };
    inlierCount: number;
    points: Vector3Like[];
  }

  interface AutoPolyValue {
    polygons: Polygon[];
    maskCorners: Vector3Like[];
    imageWidth: number;
    imageHeight: number;
    detectedFloor: PlaneDetectionResult | null;
    detectedCeiling: PlaneDetectionResult | null;
    floorGroupIdentifier: string | null;
  }

  const onAutoPolyConfirmed = useCallback(
    ({ value }: { value?: AutoPolyValue }) => {
      if (!value || !value.polygons || value.polygons.length === 0 || !viewer) return;

      console.log(`[usePotreeEvents] Auto poly confirmed, creating room with ${value.polygons.length} polygon(s)`);

      // Use detected floor plane if available, otherwise fall back to active 2D plane
      const floorPlane = value.detectedFloor?.plane;
      const ceilingPlane = value.detectedCeiling?.plane;

      // Use the floor group identifier from the tool (not the currently selected group)
      const targetGroupIdentifier = value.floorGroupIdentifier;

      // Helper function to convert polygon points to world space and project to floor
      const convertPointsToWorldSpace = (polygonPoints: Point2D[]) => {
        return polygonPoints.map((point: Point2D) => {
          // Convert from mask space to world space using maskCorners
          const u = point.x / value.imageWidth;
          const v = point.y / value.imageHeight;

          const topLeft = value.maskCorners[0];
          const topRight = value.maskCorners[1];
          const bottomRight = value.maskCorners[2];
          const bottomLeft = value.maskCorners[3];

          const top = topLeft.clone().lerp(topRight, u);
          const bottom = bottomLeft.clone().lerp(bottomRight, u);
          let worldPos = top.lerp(bottom, v);

          // Project onto detected floor plane
          if (floorPlane) {
            const planeNormal = floorPlane.normal;
            const planeConstant = floorPlane.constant;

            // Project point onto plane: distance from point to plane = (P · normal) + constant
            const distance = worldPos.x * planeNormal.x + worldPos.y * planeNormal.y + worldPos.z * planeNormal.z + planeConstant;
            worldPos = worldPos.clone().sub(planeNormal.clone().multiplyScalar(distance));
          }

          return {
            position: { x: worldPos.x, y: worldPos.y, z: worldPos.z },
            pointCloudId: null,
          };
        });
      };

      // Create room object with detected floor and ceiling planes
      if (!floorPlane || !ceilingPlane) {
        console.warn('[usePotreeEvents] Cannot create room without floor and ceiling planes');
        return;
      }

      // Create room for each polygon
      for (const polygon of value.polygons) {
        const polygonPoints = polygon.points || polygon;
        const points = convertPointsToWorldSpace(polygonPoints);

        // Convert points to InputPosition format (remove pointCloudId)
        const polygonPositions = points.map((p) => ({ x: p.position.x, y: p.position.y, z: p.position.z }));

        // Calculate position on floor plane (use first polygon point projected to floor)
        const floorPosition = polygonPositions[0];

        // Calculate position on ceiling plane (same X,Y, but at ceiling height)
        const ceilingHeight = -ceilingPlane.constant;
        const ceilingPosition = { x: floorPosition.x, y: floorPosition.y, z: ceilingHeight };

        const roomIdentifier = `auto_room_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        createAnnotation(
          buildCreateRoomParams({
            identifier: roomIdentifier,
            polygon: polygonPositions,
            base: {
              normal: { x: floorPlane.normal.x, y: floorPlane.normal.y, z: floorPlane.normal.z },
              position: floorPosition,
            },
            ceiling: {
              normal: { x: ceilingPlane.normal.x, y: ceilingPlane.normal.y, z: ceilingPlane.normal.z },
              position: ceilingPosition,
            },
            name: translations.roomAsBuilt,
            color: defaultColorHex,
            groupIdentifier: targetGroupIdentifier || undefined,
          }),
        );

        // Note: Holes in rooms are not supported yet
        // If polygon.children exists, we would need to handle them differently
        if (polygon.children && polygon.children.length > 0) {
          console.warn('[usePotreeEvents] Room holes are not yet supported, skipping holes');
        }
      }
    },
    [viewer, createAnnotation, defaultColorHex, translations.roomAsBuilt],
  );

  // Floor isolation visibility is handled directly by FloorplanPersona
  // (see FloorplanPersona.isolateFloorVisibility / restoreFloorVisibility)

  // Handle surface_isolate_visibility event from SurfacePersona
  const onSurfaceIsolateVisibility = useCallback(
    ({ value }: { value?: { groupIdentifier: string | null; isolate: boolean } }) => {
      if (!viewer || !project?.id) return;
      const as = annotationStateRef.current;

      const groups = as?.state?.groups || [];
      const annotations = as?.state?.annotations || [];
      const surfaces = viewer.scene.surfaces || [];

      // Get all surface groups
      const surfaceGroups = groups.filter((g) => g.type === 'SURFACE');

      for (const annotation of annotations) {
        if (!annotation.groupIdentifier) continue;
        const isSurfaceAnnotation = surfaceGroups.some((sg) => sg.identifier === annotation.groupIdentifier);
        if (!isSurfaceAnnotation) continue;

        const shouldBeVisible = value?.isolate && value.groupIdentifier ? annotation.groupIdentifier === value.groupIdentifier : true;

        // Update viewer object
        const surface = surfaces.find((s) => s.identifier === annotation.identifier);
        if (surface) surface.visible = shouldBeVisible;

        // Update Apollo cache directly per annotation
        const cacheId = apolloClient.cache.identify({
          __typename: annotation.__typename,
          identifier: annotation.identifier,
        });
        if (cacheId) {
          apolloClient.cache.modify({
            id: cacheId,
            fields: { visible: () => shouldBeVisible },
          });
        }
      }
    },
    [viewer, project?.id, apolloClient],
  );

  // Handle select_group event from potree (e.g., from FloorSelectorTool)
  const onSelectGroup = useCallback(
    ({ value }: { value?: { groupIdentifier: string } }) => {
      if (!value || !value.groupIdentifier) return;

      // Select the group in the hierarchy
      onClickAnnotation({ identifier: value.groupIdentifier, type: 'group' }, { isCtrl: false, isShift: false });
    },
    [onClickAnnotation],
  );

  // Handle deselect_all event from potree
  const onDeselectAll = useCallback(() => {
    // Deselect all (clear selection)
    onClickAnnotation({ identifier: '', type: 'annotation' }, { isCtrl: false, isShift: false });
  }, [onClickAnnotation]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('measurement_finish', onMeasurementFinish);
    return () => {
      viewer.scene.removeEventListener('measurement_finish', onMeasurementFinish);
    };
  }, [onMeasurementFinish, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('measurement_move_point', onMeasurementMovePoint);
    return () => {
      viewer.scene.removeEventListener('measurement_move_point', onMeasurementMovePoint);
    };
  }, [onMeasurementMovePoint, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('measurement_add_point', onMeasurementAddPoint);
    return () => {
      viewer.scene.removeEventListener('measurement_add_point', onMeasurementAddPoint);
    };
  }, [onMeasurementAddPoint, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('room_move_point', onRoomMovePoint);
    return () => {
      viewer.scene.removeEventListener('room_move_point', onRoomMovePoint);
    };
  }, [onRoomMovePoint, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('room_add_point', onRoomAddPoint);
    return () => {
      viewer.scene.removeEventListener('room_add_point', onRoomAddPoint);
    };
  }, [onRoomAddPoint, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('room_delete_point', onRoomDeletePoint);
    return () => {
      viewer.scene.removeEventListener('room_delete_point', onRoomDeletePoint);
    };
  }, [onRoomDeletePoint, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('room_updated', onRoomUpdated);
    return () => {
      viewer.scene.removeEventListener('room_updated', onRoomUpdated);
    };
  }, [onRoomUpdated, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('create_outline', onCreateOutline);
    viewer.scene.addEventListener('outline_updated', onOutlineUpdated);
    return () => {
      viewer.scene.removeEventListener('create_outline', onCreateOutline);
      viewer.scene.removeEventListener('outline_updated', onOutlineUpdated);
    };
  }, [onCreateOutline, onOutlineUpdated, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('door_window_updated', onDoorWindowUpdated);
    return () => {
      viewer.scene.removeEventListener('door_window_updated', onDoorWindowUpdated);
    };
  }, [onDoorWindowUpdated, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('measurement_delete_point', onMeasurementDeletePoint);
    return () => {
      viewer.scene.removeEventListener('measurement_delete_point', onMeasurementDeletePoint);
    };
  }, [onMeasurementDeletePoint, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('measurement_set_points', onMeasurementSetPoints);
    return () => {
      viewer.scene.removeEventListener('measurement_set_points', onMeasurementSetPoints);
    };
  }, [onMeasurementSetPoints, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('measurement_delete', onMeasurementDeleteAnnotation);
    return () => {
      viewer.scene.removeEventListener('measurement_delete', onMeasurementDeleteAnnotation);
    };
  }, [onMeasurementDeleteAnnotation, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('measurement_select', onMeasurementSelectAnnotation);
    return () => {
      viewer.scene.removeEventListener('measurement_select', onMeasurementSelectAnnotation);
    };
  }, [onMeasurementSelectAnnotation, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('annotations_box_select', onAnnotationsBoxSelect);
    return () => {
      viewer.scene.removeEventListener('annotations_box_select', onAnnotationsBoxSelect);
    };
  }, [onAnnotationsBoxSelect, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('wall_sketch_element_selected', onWallSketchElementSelected);
    return () => {
      viewer.scene.removeEventListener('wall_sketch_element_selected', onWallSketchElementSelected);
    };
  }, [onWallSketchElementSelected, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('pointorama_box_placed', onVolumePlace);
    return () => {
      viewer.scene.removeEventListener('pointorama_box_placed', onVolumePlace);
    };
  }, [onVolumePlace, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('pointorama_box_moved', onVolumeMove);
    return () => {
      viewer.scene.removeEventListener('pointorama_box_moved', onVolumeMove);
    };
  }, [onVolumeMove, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('pointorama_pointCluster_added', onPointClusterAdded);
    return () => {
      viewer.scene.removeEventListener('pointorama_pointCluster_added', onPointClusterAdded);
    };
  }, [onPointClusterAdded, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('pointorama_pointCluster_edit', onPointClusterEdit);
    return () => {
      viewer.scene.removeEventListener('pointorama_pointCluster_edit', onPointClusterEdit);
    };
  }, [onPointClusterEdit, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('create_surface', onCreateSurface);
    return () => {
      viewer.scene.removeEventListener('create_surface', onCreateSurface);
    };
  }, [onCreateSurface, viewer]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('create_floor', onCreateFloor);
    return () => {
      viewer.scene.removeEventListener('create_floor', onCreateFloor);
    };
  }, [onCreateFloor, viewer]);

  // useEffect(() => {
  //   if (!viewer) return;
  //   viewer.scene.addEventListener('performance_check', onPerformanceCheck);
  //   return () => {
  //     viewer.scene.removeEventListener('performance_check', onPerformanceCheck);
  //   };
  // }, [onPerformanceCheck, viewer]);

  //  Create event for every specific hint in potree, so their text translations can be handled here.
  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('pointorama_hint_show_plane_selection', onSetSelectPlaneHint);
    return () => {
      viewer.scene.removeEventListener('pointorama_hint_show_plane_selection', onSetSelectPlaneHint);
    };
  }, [viewer, onSetSelectPlaneHint]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('pointorama_clear_hint', onClearHint);
    return () => {
      viewer.scene.removeEventListener('pointorama_clear_hint', onClearHint);
    };
  }, [viewer, onClearHint]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('pointorama_floorplan_no_ground_plane_warning', onFloorplanNoGroundPlaneWarning);
    return () => {
      viewer.scene.removeEventListener('pointorama_floorplan_no_ground_plane_warning', onFloorplanNoGroundPlaneWarning);
    };
  }, [viewer, onFloorplanNoGroundPlaneWarning]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('door_window_overlap_rejected', onDoorWindowOverlapRejected);
    return () => {
      viewer.scene.removeEventListener('door_window_overlap_rejected', onDoorWindowOverlapRejected);
    };
  }, [viewer, onDoorWindowOverlapRejected]);

  const onFloorAddedNotification = useCallback(
    ({ value }: { value?: { floorHeight: string; floorsRemaining: number } }) => {
      if (!value) return;
      showToast({
        title: translations.floorAddedTitle,
        text: translations.floorAddedText.replace('{height}', value.floorHeight),
        type: ToastType.SUCCESS,
      });
    },
    [showToast, translations.floorAddedTitle, translations.floorAddedText],
  );

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('floor_added_notification', onFloorAddedNotification);
    return () => {
      viewer.scene.removeEventListener('floor_added_notification', onFloorAddedNotification);
    };
  }, [viewer, onFloorAddedNotification]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('create_room', onAutoPolyConfirmed);
    return () => {
      viewer.scene.removeEventListener('create_room', onAutoPolyConfirmed);
    };
  }, [viewer, onAutoPolyConfirmed]);

  // Handle create_structure event from StructureKindAdapter — auto-classified
  // beam/column with height resolved from the containing room (or default for
  // beams without a room). CUSTOM is only set later via the right-panel.
  interface CreateStructureValue {
    polygon: { x: number; y: number; z: number }[];
    height: number;
    kind: StructureKind;
    roomIdentifier?: string | null;
    groupIdentifier?: string | null;
  }

  const onCreateStructure = useCallback(
    ({ value }: { value?: CreateStructureValue }) => {
      if (!value || !value.polygon || value.polygon.length < 3) return;

      const identifier = `structure_${uuid()}`;
      const name = value.kind === StructureKind.BEAM ? translations.beam : value.kind === StructureKind.COLUMN ? translations.column : translations.structure;
      createAnnotation(
        buildCreateStructureParams({
          identifier,
          polygon: value.polygon,
          height: value.height,
          kind: value.kind,
          roomId: value.roomIdentifier ?? null,
          name,
          color: defaultColorHex,
          groupIdentifier: value.groupIdentifier ?? undefined,
        }),
      );
    },
    [createAnnotation, defaultColorHex, translations.beam, translations.column, translations.structure],
  );

  // Persist an edited structure footprint. The adapter has already applied the
  // new polygon to the shape (keeping its base Z); we flush it to the reactive
  // property. Height and kind are intentionally NOT recomputed on edit.
  const onStructureUpdated = useCallback((event: { type: string; structure?: { identifier: string; getPolygon: () => { x: number; y: number; z: number }[] } }) => {
    if (!event.structure) return;
    try {
      const service = getGlobalPropertyService();
      if (!service) return;
      getStructurePolygonProperty(event.structure.identifier).value = event.structure.getPolygon().map((p) => ({ x: p.x, y: p.y, z: p.z }));
    } catch {
      // Property service not ready
    }
  }, []);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('create_structure', onCreateStructure);
    viewer.scene.addEventListener('structure_updated', onStructureUpdated);
    return () => {
      viewer.scene.removeEventListener('create_structure', onCreateStructure);
      viewer.scene.removeEventListener('structure_updated', onStructureUpdated);
    };
  }, [viewer, onCreateStructure, onStructureUpdated]);

  const onBoxBreaksShape = useCallback(() => {
    showToast({
      title: translations.boxRevertedTitle,
      text: translations.boxRevertedText,
      type: ToastType.WARNING,
    });
  }, [showToast, translations.boxRevertedTitle, translations.boxRevertedText]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('box_breaks_shape_notification', onBoxBreaksShape);
    return () => {
      viewer.scene.removeEventListener('box_breaks_shape_notification', onBoxBreaksShape);
    };
  }, [viewer, onBoxBreaksShape]);

  // Edge-op (parallel / perpendicular alignment) reverted: the requested
  // adjustment would have produced an invalid polygon and was rolled back —
  // without this toast the rollback is invisible when the edges were
  // already nearly aligned.
  const onEdgeOpReverted = useCallback(() => {
    showToast({
      title: translations.alignmentRevertedTitle,
      text: translations.alignmentRevertedText,
      type: ToastType.WARNING,
    });
  }, [showToast, translations.alignmentRevertedTitle, translations.alignmentRevertedText]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('edge_op_reverted_notification', onEdgeOpReverted);
    return () => {
      viewer.scene.removeEventListener('edge_op_reverted_notification', onEdgeOpReverted);
    };
  }, [viewer, onEdgeOpReverted]);

  // Handle request_skeleton event from WallSketchEditorTool
  // Checks if a skeleton annotation exists for the floor, creates one if not
  interface RequestWallSketchValue {
    floorGroupIdentifier: string;
    floorHeight: number;
    roomIdentifiers: string[];
  }

  // Track floor groups that have wallSketches (either existing or being created)
  // This is a persistent set that survives callback recreation
  const wallSketchCreatedForFloors = useRef<Set<string>>(new Set());

  // Initialize the wallSketchCreatedForFloors set with existing wallSketches when project loads
  useEffect(() => {
    const as = annotationStateRef.current;
    if (!as?.state?.annotations) return;

    as.state.annotations.forEach((annotation) => {
      if (annotation.type === 'WALL_SKETCH' && annotation.groupIdentifier) {
        wallSketchCreatedForFloors.current.add(annotation.groupIdentifier);
      }
    });
  }, [project?.id]); // Only run when project ID changes (initial load or project switch)

  const onRequestWallSketch = useCallback(
    ({ value }: { value?: RequestWallSketchValue }) => {
      if (!value) return;

      const { floorGroupIdentifier } = value;

      // Check if we already created or are creating a skeleton for this floor group
      if (wallSketchCreatedForFloors.current.has(floorGroupIdentifier)) {
        return;
      }

      // Check if a skeleton annotation already exists for this floor group in the cache
      const existingWallSketch = annotationStateRef.current?.state?.annotations?.find((annotation) => annotation.type === 'WALL_SKETCH' && annotation.groupIdentifier === floorGroupIdentifier);

      if (existingWallSketch) {
        // WallSketch already exists, mark it and return
        wallSketchCreatedForFloors.current.add(floorGroupIdentifier);
        return;
      }

      // Mark as created BEFORE calling executeAction to prevent race conditions
      wallSketchCreatedForFloors.current.add(floorGroupIdentifier);

      // Create new skeleton annotation for this floor
      const wallSketchIdentifier = `skeleton_${floorGroupIdentifier}_${uuid()}`;

      createAnnotation(
        buildCreateWallSketchParams({
          identifier: wallSketchIdentifier,
          nodes: [],
          edges: [],
          name: translations.wallSketch,
          color: defaultColorHex,
          groupIdentifier: floorGroupIdentifier,
        }),
      );

      // A FRESH sketch resets the room-folder tombstones on this floor.
      // Deleting the previous sketch cascaded the room folders away and
      // wrote `ROOM_GROUP_LINK_NONE` links so reconciliation wouldn't
      // instantly recreate them from the surviving room measures — but a
      // new sketch is a fresh start: clear the suppression so
      // useRoomFolderSync auto-generates the folders again.
      const floorRooms = annotationStateRef.current?.state?.annotations?.filter((a) => a.type === 'ROOM' && a.groupIdentifier === floorGroupIdentifier) ?? [];
      for (const room of floorRooms) {
        try {
          const link = getRoomGroupLinkProperty(room.identifier);
          if (link.value === ROOM_GROUP_LINK_NONE) link.value = '';
        } catch {
          // property service not ready — the tombstone stays until the next
          // sketch creation; acceptable degradation
        }
      }
    },
    [createAnnotation, defaultColorHex, translations.wallSketch],
  );

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('request_skeleton', onRequestWallSketch);
    return () => {
      viewer.scene.removeEventListener('request_skeleton', onRequestWallSketch);
    };
  }, [viewer, onRequestWallSketch]);

  // Handle request_opening_sketch — fired by replicateOpeningSketchesForFloor
  // during a floor→sketch conversion to create the OPENING_SKETCH equivalent of
  // a floorplan OPENING. Creation is idempotent at the source (the replicator
  // skips openings already replicated), so this just creates the annotation.
  const onRequestOpeningSketch = useCallback(
    (event: { value?: OpeningSketchRequest }) => {
      const value = event.value;
      if (!value) return;
      // Reuse the caller-provided identifier when present (e.g. undo/redo
      // re-creating a deleted opening), else mint a fresh one.
      const identifier = value.identifier || `opening_sketch_${value.groupIdentifier}_${uuid()}`;
      // Auto-file into the adjacent room's folder, picked by SIDE: the room on
      // the swing/inset side wins (a door files into the room it opens INTO),
      // else the room on the other side (an exterior-wall opening files into
      // the interior room it's attached to). Fallbacks: the edge's roomIds,
      // then the floor group.
      const skeleton = viewer?.scene.wallSketches.find((s) => s.groupIdentifier === value.groupIdentifier);
      const roomGroupId = viewer
        ? (resolveRoomGroupForOpeningSpan(viewer, value.groupIdentifier, { edgeId: value.wallEdgeId, offset: value.offset, length: value.length, swingPositive: value.swingPositive }) ??
          resolveRoomGroupForWallEdge(viewer, value.groupIdentifier, skeleton?.getEdge(value.wallEdgeId)?.roomIds))
        : null;
      createAnnotation(
        buildCreateOpeningSketchParams({
          identifier,
          // Preserve the source opening's (possibly user-renamed) name when
          // replicating; hand-drawn openings fall back to the kind default.
          name: value.name || (value.kind === OpeningKind.WINDOW ? translations.windowName : translations.doorName),
          color: defaultColorHex,
          groupIdentifier: roomGroupId ?? value.groupIdentifier,
          wallEdgeId: value.wallEdgeId,
          offset: value.offset,
          length: value.length,
          lowerBound: value.lowerBound,
          height: value.height,
          inset: value.inset,
          kind: value.kind,
          doorType: value.doorType,
          windowType: value.windowType,
          hingeAtStart: value.hingeAtStart,
          swingPositive: value.swingPositive,
          rollable: value.rollable,
          sourceOpeningId: value.sourceOpeningId,
        }),
      );
    },
    [createAnnotation, defaultColorHex, viewer, translations.doorName, translations.windowName],
  );

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('request_opening_sketch', onRequestOpeningSketch);
    return () => {
      viewer.scene.removeEventListener('request_opening_sketch', onRequestOpeningSketch);
    };
  }, [viewer, onRequestOpeningSketch]);

  // Handle request_sketch_object — fired by the SketchObjectTool when placing
  // a furniture/fixture symbol (and by its undo/redo re-creating a deleted one,
  // which is why the caller-provided identifier is reused).
  const onRequestSketchObject = useCallback(
    (event: { value?: SketchObjectRequest }) => {
      const value = event.value;
      if (!value) return;
      const identifier = value.identifier || `sketch_object_${value.groupIdentifier}_${uuid()}`;
      const type = getSketchObjectType(value.typeId);
      // Registry labels are English source strings — localize the default name.
      // Dimensions are named for what they are, not their style.
      const baseName = type?.dimension ? t('Dimension') : (type?.label ?? value.typeId);
      const localizedBaseName = viewer ? viewer.translationService.t(baseName) : baseName;
      // Auto-file into the containing room's folder. FREE placements classify
      // by the anchor midpoint (the object's 2D center); WALL-mounted objects
      // (sinks, toilets, …) have no anchor — their body center is derived
      // from the wall placement instead (attachment point pushed off the wall
      // face into the room the object protrudes into).
      const anchorMid =
        value.anchor && value.anchor.length > 0
          ? {
              x: value.anchor.reduce((s, p) => s + p.x, 0) / value.anchor.length,
              y: value.anchor.reduce((s, p) => s + p.y, 0) / value.anchor.length,
            }
          : null;
      const classifyPoint = (value.wall && viewer ? wallObjectCenterPoint(viewer, value.groupIdentifier, value.wall, value.width, value.depth) : null) ?? anchorMid;
      const roomGroupId = classifyPoint && viewer ? resolveRoomGroupForPoint(viewer, value.groupIdentifier, classifyPoint) : null;
      createAnnotation(
        buildCreateSketchObjectParams({
          identifier,
          name: value.name || localizedBaseName,
          color: defaultColorHex,
          groupIdentifier: roomGroupId ?? value.groupIdentifier,
          typeId: value.typeId,
          width: value.width,
          depth: value.depth,
          anchor: value.anchor,
          rotation: value.rotation,
          wall: value.wall,
          paramValues: value.paramValues,
          sourceStructureId: value.sourceStructureId,
        }),
      );
    },
    [createAnnotation, defaultColorHex, viewer],
  );

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('request_sketch_object', onRequestSketchObject);
    return () => {
      viewer.scene.removeEventListener('request_sketch_object', onRequestSketchObject);
    };
  }, [viewer, onRequestSketchObject]);

  /**
   * Re-file a sketch annotation after a viewport move: the containing room's
   * folder wins, else the floor group. A move through the viewport is what
   * clears a manual left-panel override. Skips the mutation when the target
   * equals the annotation's current group.
   */
  const refileAnnotation = useCallback(
    (identifier: string, targetGroupId: string | null) => {
      if (!targetGroupId) return;
      const current = annotationsByIdentifierRef.current[identifier]?.groupIdentifier ?? null;
      if (current === targetGroupId) return;
      void moveAnnotationsToGroup([identifier], targetGroupId);
    },
    [moveAnnotationsToGroup],
  );

  const onSketchObjectMoved = useCallback(
    (event: { value?: { identifier: string } }) => {
      const identifier = event.value?.identifier;
      if (!identifier || !viewer) return;
      const object = viewer.scene.sketchObjects.find((o) => o.identifier === identifier);
      // The scene object's groupIdentifier is already floor-resolved (sync).
      const floorId = object?.groupIdentifier ?? null;
      const center = object?.resolveFrame()?.center;
      if (!object || !floorId || !center) return;
      const roomGroupId = resolveRoomGroupForPoint(viewer, floorId, center);
      refileAnnotation(identifier, roomGroupId ?? floorId);
    },
    [viewer, refileAnnotation],
  );

  const onOpeningSketchMoved = useCallback(
    (event: { value?: { identifier: string } }) => {
      const identifier = event.value?.identifier;
      if (!identifier || !viewer) return;
      const opening = viewer.scene.openingSketches.find((o) => o.identifier === identifier);
      const floorId = opening?.groupIdentifier ?? null;
      if (!opening || !floorId) return;
      let edgeId = '';
      let offset = 0;
      let length = 0;
      let swingPositive = true;
      try {
        edgeId = getOpeningSketchWallEdgeIdProperty(identifier).value || '';
        offset = getOpeningSketchOffsetProperty(identifier).value ?? 0;
        length = getOpeningSketchLengthProperty(identifier).value ?? 0;
        swingPositive = getOpeningSketchSwingPositiveProperty(identifier).value;
      } catch {
        return;
      }
      // Same side-aware pick as creation: swing/inset-side room first, other
      // side second (exterior walls), edge roomIds as the legacy fallback.
      const skeleton = viewer.scene.wallSketches.find((s) => s.groupIdentifier === floorId);
      const roomGroupId =
        resolveRoomGroupForOpeningSpan(viewer, floorId, { edgeId, offset, length, swingPositive }) ?? resolveRoomGroupForWallEdge(viewer, floorId, skeleton?.getEdge(edgeId)?.roomIds);
      refileAnnotation(identifier, roomGroupId ?? floorId);
    },
    [viewer, refileAnnotation],
  );

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('sketch_object_moved', onSketchObjectMoved);
    viewer.scene.addEventListener('opening_sketch_moved', onOpeningSketchMoved);
    return () => {
      viewer.scene.removeEventListener('sketch_object_moved', onSketchObjectMoved);
      viewer.scene.removeEventListener('opening_sketch_moved', onOpeningSketchMoved);
    };
  }, [viewer, onSketchObjectMoved, onOpeningSketchMoved]);

  // Handle request_room_tag — fired by the RoomTagTool (Design tab): create a
  // manual room tag (ROOM group) anchored at the clicked position.
  const onRequestRoomTag = useCallback(
    (event: { value?: { identifier: string; floorGroupIdentifier: string; position: { x: number; y: number; z: number } } }) => {
      const value = event.value;
      if (!value) return;
      // `ready` reports create success — the editor must not send a rename
      // for a folder whose create failed (server rejection, network).
      const ready = createGroup({ identifier: value.identifier, name: translations.roomName, type: GroupType.Room, annotationIdentifiers: [], parentIdentifier: value.floorGroupIdentifier })
        .then(() => {
          try {
            getRoomGroupPositionProperty(value.identifier).value = { ...value.position };
          } catch {
            // property service not ready — the tag stays unplaced until dragged
          }
          return true;
        })
        .catch(() => false);
      // Open the inline name editor immediately with the default name
      // selected; a rename typed before the create confirms awaits `ready`.
      viewer?.scene.dispatchEvent({
        type: 'room_tag_edit',
        value: { folderId: value.identifier, name: translations.roomName, position: value.position, ready } satisfies RoomTagEditRequest,
      });
    },
    [createGroup, viewer, translations.roomName],
  );

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('request_room_tag', onRequestRoomTag);
    return () => {
      viewer.scene.removeEventListener('request_room_tag', onRequestRoomTag);
    };
  }, [viewer, onRequestRoomTag]);

  // Handle edit_skeleton event from WallSketchEditorTool
  // Saves skeleton nodes and edges to the database
  interface EditWallSketchValue {
    annotationIdentifier: string;
    nodes: Array<{
      id: string;
      name: string;
      position: { x: number; y: number; z: number };
      roomIds: string[];
      edgeIds: string[];
      metadata?: string;
    }>;
    edges: Array<{
      id: string;
      name: string;
      startNodeId: string;
      endNodeId: string;
      roomIds: string[];
      leftSize: number;
      rightSize: number;
      wallType: SkeletonWallType;
      metadata?: string;
    }>;
    /** Optional skeleton-level metadata JSON (e.g. linked outline id). */
    metadata?: string;
  }

  const onEditWallSketch = useCallback(({ value }: { value?: EditWallSketchValue }) => {
    if (!value) return;
    const { annotationIdentifier, nodes, edges, metadata } = value;
    try {
      const service = getGlobalPropertyService();
      if (service) {
        getWallSketchNodesProperty(annotationIdentifier).value = nodes;
        getWallSketchEdgesProperty(annotationIdentifier).value = edges;
        if (metadata !== undefined) {
          getWallSketchMetadataProperty(annotationIdentifier).value = metadata;
        }
      } else {
        console.error(`edit_skeleton: no property service — the geometry of ${annotationIdentifier} (${nodes.length} node(s), ${edges.length} edge(s)) was NOT saved`);
      }
    } catch (e) {
      // Usually the property service not being ready yet — but never silently: a wall whose geometry is
      // not saved stays an empty object, present in the tree and invisible in the view.
      console.error(`edit_skeleton: saving the geometry of ${annotationIdentifier} (${nodes.length} node(s), ${edges.length} edge(s)) failed`, e);
    }
  }, []);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('edit_skeleton', onEditWallSketch);
    return () => {
      viewer.scene.removeEventListener('edit_skeleton', onEditWallSketch);
    };
  }, [viewer, onEditWallSketch]);

  // Handle wall_sketch_removed event - clear the floor from wallSketchCreatedForFloors
  // so that a new skeleton can be created for this floor
  const onWallSketchRemoved = useCallback((event: { type: string; groupIdentifier?: string }) => {
    if (event.groupIdentifier) {
      wallSketchCreatedForFloors.current.delete(event.groupIdentifier);
    }
  }, []);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('wall_sketch_removed', onWallSketchRemoved);
    return () => {
      viewer.scene.removeEventListener('wall_sketch_removed', onWallSketchRemoved);
    };
  }, [viewer, onWallSketchRemoved]);

  // Floor isolation visibility listener removed — handled by FloorplanPersona directly

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('surface_isolate_visibility', onSurfaceIsolateVisibility);
    return () => {
      viewer.scene.removeEventListener('surface_isolate_visibility', onSurfaceIsolateVisibility);
    };
  }, [viewer, onSurfaceIsolateVisibility]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('select_group', onSelectGroup);
    return () => {
      viewer.scene.removeEventListener('select_group', onSelectGroup);
    };
  }, [viewer, onSelectGroup]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('deselect_all', onDeselectAll);
    return () => {
      viewer.scene.removeEventListener('deselect_all', onDeselectAll);
    };
  }, [viewer, onDeselectAll]);

  // Derive groupsJson from the annotation tree context (reactive) instead of readAnnotationState()
  // (which only updates when usePotreeEvents re-renders — but it doesn't subscribe to annotation state).
  const annotationTree = useAnnotationTree();
  const groupsJson = useMemo(() => {
    const groups = annotationTree.filter((item) => isAnnotationContextGroup(item));
    return JSON.stringify(groups.map((g) => ({ identifier: g.identifier, type: g.type, name: g.name })));
  }, [annotationTree]);

  // Dispatch objects_selection_changed when selection changes
  useEffect(() => {
    // Read fresh from Apollo cache (not the stale annotationStateRef)
    const as = readAnnotationState();
    if (!viewer || !as) return;

    // Determine the selected floor group (either directly or via annotation parent)
    let selectedFloorGroupIdentifier: string | undefined;

    // Check if a Floor group is directly selected
    if (selectedGroupIdentifier) {
      const group = as.state.groups?.find((g) => g.identifier === selectedGroupIdentifier);
      if (group?.type === GroupType.Floor) {
        selectedFloorGroupIdentifier = selectedGroupIdentifier;
      }
    }

    // Check if an annotation is selected that belongs to a Floor group
    if (!selectedFloorGroupIdentifier && selectedAnnotationIdentifier) {
      const selectedAnnotation = annotationsByIdentifier[selectedAnnotationIdentifier];
      if (selectedAnnotation?.groupIdentifier) {
        const parentGroup = as.state.groups?.find((g) => g.identifier === selectedAnnotation.groupIdentifier && g.type === GroupType.Floor);
        if (parentGroup) {
          selectedFloorGroupIdentifier = parentGroup.identifier;
        }
      }
    }

    // Check if multiple annotations are selected that all belong to the same Floor group
    if (!selectedFloorGroupIdentifier && allSelectedAnnotations.length > 1) {
      const floorGroupIdentifiers = new Set<string>();
      for (const annotation of allSelectedAnnotations) {
        if (annotation.groupIdentifier) {
          const parentGroup = as.state.groups?.find((g) => g.identifier === annotation.groupIdentifier && g.type === GroupType.Floor);
          if (parentGroup) {
            floorGroupIdentifiers.add(parentGroup.identifier);
          }
        }
      }
      // Only set floor if all selected annotations belong to the same floor group
      if (floorGroupIdentifiers.size === 1) {
        selectedFloorGroupIdentifier = Array.from(floorGroupIdentifiers)[0];
      }
    }

    // Get floor data if a floor is selected
    let floorData: { floor: FloorPlaneData; ceiling?: FloorPlaneData; roomIdentifiers?: string[] } | undefined;
    if (selectedFloorGroupIdentifier) {
      const floorGroupsData = loadFloorGroupsData();
      const floorGroup = floorGroupsData.find((g) => g.groupIdentifier === selectedFloorGroupIdentifier);
      if (floorGroup) {
        // Get room identifiers that belong to this floor group
        const roomIdentifiers = (as.state.annotations || []).filter((a) => a.type === 'ROOM' && a.groupIdentifier === selectedFloorGroupIdentifier).map((a) => a.identifier);
        floorData = { floor: floorGroup.floor, ceiling: floorGroup.ceiling, roomIdentifiers };
      }
    }

    // Determine the selected surface group (either directly or via annotation parent)
    let selectedSurfaceIdentifier: string | undefined;
    let surface: { position: { x: number; y: number; z: number }; normal: { x: number; y: number; z: number } } | undefined;

    // Check if a Surface group is directly selected
    if (selectedGroupIdentifier) {
      const group = as.state.groups?.find((g) => g.identifier === selectedGroupIdentifier);
      if (group?.type === GroupType.Surface) {
        selectedSurfaceIdentifier = selectedGroupIdentifier;
      }
    }

    // Check if an annotation is selected that belongs to a Surface group
    if (!selectedSurfaceIdentifier && selectedAnnotationIdentifier) {
      const selectedAnnotation = annotationsByIdentifier[selectedAnnotationIdentifier];
      if (selectedAnnotation?.groupIdentifier) {
        const parentGroup = as.state.groups?.find((g) => g.identifier === selectedAnnotation.groupIdentifier && g.type === GroupType.Surface);
        if (parentGroup) {
          selectedSurfaceIdentifier = parentGroup.identifier;
        }
      }
    }

    // Check if multiple annotations are selected that all belong to the same Surface group
    if (!selectedSurfaceIdentifier && allSelectedAnnotations.length > 1) {
      const surfaceGroupIdentifiers = new Set<string>();
      for (const annotation of allSelectedAnnotations) {
        if (annotation.groupIdentifier) {
          const parentGroup = as.state.groups?.find((g) => g.identifier === annotation.groupIdentifier && g.type === GroupType.Surface);
          if (parentGroup) {
            surfaceGroupIdentifiers.add(parentGroup.identifier);
          }
        }
      }
      // Only set surface if all selected annotations belong to the same surface group
      if (surfaceGroupIdentifiers.size === 1) {
        selectedSurfaceIdentifier = Array.from(surfaceGroupIdentifiers)[0];
      }
    }

    // Get surface data if a surface is selected
    if (selectedSurfaceIdentifier) {
      const surfacesData = loadSurfacesFromGroups();
      const surfaceData = surfacesData.find((s) => s.groupIdentifier === selectedSurfaceIdentifier);
      if (surfaceData) {
        surface = {
          position: surfaceData.plane.position,
          normal: surfaceData.plane.normal,
        };
      }
    }

    viewer.scene.dispatchEvent({
      type: 'objects_selection_changed',
      selectedGroupIdentifier,
      selectedAnnotationIdentifier,
      selectedFloorGroupIdentifier,
      floorData,
      selectedSurfaceIdentifier,
      surface,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer, selectedGroupIdentifier, selectedAnnotationIdentifier, annotationsByIdentifier, groupsJson, allSelectedAnnotations]);

  // Handle requests for objects list (groups)
  const onObjectsRequest = useCallback(() => {
    // Read fresh from Apollo cache (not the stale annotationStateRef)
    const as = readAnnotationState();
    if (!viewer || !as) return;

    // Dispatch the objects changed event with current groups, floor data, and surface data
    viewer.scene.dispatchEvent({
      type: 'objects_changed',
      groups: as.state.groups || [],
      floorGroupsData: loadFloorGroupsData(),
      surfacesData: loadSurfacesFromGroups(),
    });

    // Also dispatch current selection state
    let selectedFloorGroupIdentifier: string | undefined;

    if (selectedGroupIdentifier) {
      const group = as.state.groups?.find((g) => g.identifier === selectedGroupIdentifier);
      if (group?.type === GroupType.Floor) {
        selectedFloorGroupIdentifier = selectedGroupIdentifier;
      }
    }

    if (!selectedFloorGroupIdentifier && selectedAnnotationIdentifier) {
      const selectedAnnotation = annotationsByIdentifier[selectedAnnotationIdentifier];
      if (selectedAnnotation?.groupIdentifier) {
        const parentGroup = as.state.groups?.find((g) => g.identifier === selectedAnnotation.groupIdentifier && g.type === GroupType.Floor);
        if (parentGroup) {
          selectedFloorGroupIdentifier = parentGroup.identifier;
        }
      }
    }

    let floorData: { floor: FloorPlaneData; ceiling?: FloorPlaneData; roomIdentifiers?: string[] } | undefined;
    if (selectedFloorGroupIdentifier) {
      const floorGroupsData = loadFloorGroupsData();
      const floorGroup = floorGroupsData.find((g) => g.groupIdentifier === selectedFloorGroupIdentifier);
      if (floorGroup) {
        const roomIdentifiers = (as.state.annotations || []).filter((a) => a.type === 'ROOM' && a.groupIdentifier === selectedFloorGroupIdentifier).map((a) => a.identifier);
        floorData = { floor: floorGroup.floor, ceiling: floorGroup.ceiling, roomIdentifiers };
      }
    }

    let selectedSurfaceIdentifier: string | undefined;
    let surface: { position: { x: number; y: number; z: number }; normal: { x: number; y: number; z: number } } | undefined;

    if (selectedGroupIdentifier) {
      const group = as.state.groups?.find((g) => g.identifier === selectedGroupIdentifier);
      if (group?.type === GroupType.Surface) {
        selectedSurfaceIdentifier = selectedGroupIdentifier;
      }
    }

    if (!selectedSurfaceIdentifier && selectedAnnotationIdentifier) {
      const selectedAnnotation = annotationsByIdentifier[selectedAnnotationIdentifier];
      if (selectedAnnotation?.groupIdentifier) {
        const parentGroup = as.state.groups?.find((g) => g.identifier === selectedAnnotation.groupIdentifier && g.type === GroupType.Surface);
        if (parentGroup) {
          selectedSurfaceIdentifier = parentGroup.identifier;
        }
      }
    }

    if (selectedSurfaceIdentifier) {
      const surfacesData = loadSurfacesFromGroups();
      const surfaceData = surfacesData.find((s) => s.groupIdentifier === selectedSurfaceIdentifier);
      if (surfaceData) {
        surface = {
          position: surfaceData.plane.position,
          normal: surfaceData.plane.normal,
        };
      }
    }

    viewer.scene.dispatchEvent({
      type: 'objects_selection_changed',
      selectedGroupIdentifier,
      selectedAnnotationIdentifier,
      selectedFloorGroupIdentifier,
      floorData,
      selectedSurfaceIdentifier,
      surface,
    });
  }, [viewer, readAnnotationState, loadFloorGroupsData, loadSurfacesFromGroups, selectedGroupIdentifier, selectedAnnotationIdentifier, annotationsByIdentifier]);

  useEffect(() => {
    if (!viewer) return;
    viewer.scene.addEventListener('objects_request', onObjectsRequest);
    return () => {
      viewer.scene.removeEventListener('objects_request', onObjectsRequest);
    };
  }, [viewer, onObjectsRequest]);

  // Dispatch objects_changed whenever project groups change
  const prevGroupsRef = useRef<string[]>([]);
  // Reach the latest loaders from inside the effects without making them dependencies,
  // so the effects still only re-run when their own data (groupsJson, floor ids) changes.
  // (useStableEvent, not React's useEffectEvent: that one goes stale inside memo components — see the hook.)
  const currentFloorGroupsData = useStableEvent(() => loadFloorGroupsData());
  const currentSurfacesData = useStableEvent(() => loadSurfacesFromGroups());
  useEffect(() => {
    if (!viewer) return;

    // Read fresh group data from the Apollo cache (not the stale annotationStateRef)
    const as = readAnnotationState();
    const currentGroups = as?.state.groups || [];
    const currentGroupIdentifiers = currentGroups.map((g) => g.identifier);

    // Check for deleted groups
    const deletedGroupIdentifiers = prevGroupsRef.current.filter((id) => !currentGroupIdentifiers.includes(id));

    // Dispatch deleted event if groups were removed
    if (deletedGroupIdentifiers.length > 0) {
      viewer.scene.dispatchEvent({
        type: 'objects_deleted',
        deletedGroupIdentifiers,
      });
    }

    // Update previous groups reference
    prevGroupsRef.current = currentGroupIdentifiers;

    viewer.scene.dispatchEvent({
      type: 'objects_changed',
      groups: currentGroups,
      floorGroupsData: currentFloorGroupsData(),
      surfacesData: currentSurfacesData(),
    });
  }, [viewer, groupsJson, readAnnotationState, currentFloorGroupsData, currentSurfacesData]);

  // Re-dispatch objects_changed when a floor's plane PROPERTIES change but its group
  // structure does not — e.g. another user moves a floor's elevation, or a floor's
  // height arrives from a remote create after the group already appeared. groupsJson
  // (above) only tracks identifier/type/name, so it doesn't fire on property-only
  // changes. The floor plane values are ReactiveProperties; setValueFromRemote calls
  // next() on them, so subscribing here catches remote updates natively — no Pusher
  // coupling. We use subscribeToChanges (not subscribe) to skip the initial value and
  // avoid a redundant dispatch on mount.
  const floorGroupIds = useMemo(() => annotationTree.filter((item) => isAnnotationContextGroup(item) && item.type === GroupType.Floor).map((g) => g.identifier), [annotationTree]);
  const floorGroupIdsJson = JSON.stringify(floorGroupIds);
  useEffect(() => {
    if (!viewer || !hasGlobalPropertyService() || floorGroupIds.length === 0) return;

    const service = getGlobalPropertyService();
    const planeProps = ['floor_plane_position', 'floor_plane_normal', 'ceiling_plane_position', 'ceiling_plane_normal'];

    const redispatch = () => {
      const as = readAnnotationState();
      viewer.scene.dispatchEvent({
        type: 'objects_changed',
        groups: as?.state.groups || [],
        floorGroupsData: currentFloorGroupsData(),
        surfacesData: currentSurfacesData(),
      });
    };

    const subscriptions = floorGroupIds.flatMap((floorId) => planeProps.map((identifier) => service.getProperty(identifier, floorId, PropertyType.PROJECT_GLOBAL).subscribeToChanges(redispatch)));

    return () => subscriptions.forEach((s) => s.unsubscribe());
    // floorGroupIdsJson is the stable key for floorGroupIds; readAnnotationState is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer, floorGroupIdsJson, currentFloorGroupsData, currentSurfacesData]);
};
